/**
 * Send a new client note to the AWS Lambda endpoint.
 *
 * @param {string} clientId      - The ID of the client.
 * @param {string} notes         - The note content to save.
 * @param {string} userUpdate    - The username or identifier of who is updating.
 * @returns {Promise<Object>}     - The JSON response from the API.
 * @throws {Error}               - If the network request fails or returns non-2xx.
 */

export async function postClientNotes(clientId, notes, userUpdate) {
  const url = "https://ygmc5rmx2b.execute-api.ap-southeast-1.amazonaws.com/prod/postClientNotes";
  const payload = {
    client_id: clientId,
    client_notes: notes,
    user_update: userUpdate,
    timestamp_update: new Date().toISOString(),
  };

  // First, try the normal CORS request
  try {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',  
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const t = await response.text();
      throw new Error(`Status ${response.status}: ${t}`);
    }
    return response.json();
  } catch (err) {
    console.warn("CORS fetch failed, retrying as no-cors…", err);

    // Fallback: send as no-cors (opaque) so the browser at least fires it off
    await fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    // We can’t read the response in no-cors, but at least it’ll hit your Lambda.
    return { ok: true };
  }
}


/**
 * Fetch notes for a given client ID.
 *
 * @param {string} clientId      - The ID of the client.
 * @returns {Promise<Array>}     - An array of note objects.
 * @throws {Error}               - If the network request fails or returns non-2xx.
 */

export async function getClientNotesById(clientId) {
  // <— use the *GET* endpoint, not the POST one
  const url = `https://wz46q4nte3.execute-api.ap-southeast-1.amazonaws.com/prod/getClientNotesById/${encodeURIComponent(clientId)}`;

  // try a normal CORS fetch
  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Status ${res.status}: ${text}`);
    }
    return res.json();    // should be your array of notes
  } catch (err) {
    console.warn('GET notes CORS fetch failed, trying no-cors fallback', err);
    // fallback just fires the request so the Lambda sees it,
    // but we can’t inspect the response body in no-cors mode
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors'
    });
    return [];            // at least don’t crash your UI
  }
}